/**
 * Health is computed on the server by these rules, never in the UI (TZ section 16).
 * Two screens disagreeing about whether a site is down is worse than either answer.
 */
export const HEALTH_STATUSES = ['healthy', 'degraded', 'down', 'unknown', 'maintenance'] as const;
export type HealthStatus = (typeof HEALTH_STATUSES)[number];

export interface HealthSignals {
  readonly maintenanceMode: boolean;
  /** Null when no probe has run or the agent is offline: absence of data, not a failure. */
  readonly httpReachable: boolean | null;
  readonly containersRunning: boolean | null;
  readonly agentOnline: boolean;
  readonly backupFreshnessBreached: boolean;
  readonly diskWarning: boolean;
  readonly recentContainerRestarts: number;
  readonly dnsDrift: boolean;
}

export const RESTART_DEGRADED_THRESHOLD = 3;

/** A certificate this close to running out is an alert, and a warning on the site's page. */
export const CERTIFICATE_WARNING_MS = 14 * 86_400_000;

export interface HealthAssessment {
  readonly status: HealthStatus;
  /** Every rule that fired, so the UI explains itself instead of showing a colour. */
  readonly reasons: readonly string[];
}

export function assessHealth(signals: HealthSignals): HealthAssessment {
  if (signals.maintenanceMode) {
    return { status: 'maintenance', reasons: ['maintenance mode is on'] };
  }

  if (signals.httpReachable === false || signals.containersRunning === false) {
    const reasons: string[] = [];
    if (signals.httpReachable === false) reasons.push('http probe failed');
    if (signals.containersRunning === false) reasons.push('containers are not running');
    return { status: 'down', reasons };
  }

  if (!signals.agentOnline || signals.httpReachable === null || signals.containersRunning === null) {
    return { status: 'unknown', reasons: ['no current data from the node agent'] };
  }

  const reasons: string[] = [];
  if (signals.backupFreshnessBreached) reasons.push('backup is older than the policy allows');
  if (signals.diskWarning) reasons.push('disk usage is above the warning threshold');
  if (signals.recentContainerRestarts >= RESTART_DEGRADED_THRESHOLD) {
    reasons.push(`container restarted ${signals.recentContainerRestarts} times recently`);
  }
  if (signals.dnsDrift) reasons.push('dns records differ from the desired state');

  if (reasons.length > 0) return { status: 'degraded', reasons };
  return { status: 'healthy', reasons: [] };
}
